import { Link } from 'react-router';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import type { CustomerIncident } from '@models/customers';
import {
  INCIDENT_STATUS_LABELS,
  getIncidentStatusTone,
  getIncidentUrl,
} from '@features/incidents/constants';
import { formatPaymentDate } from '@features/payments/helpers';

import StatusChip from '@components/ui/StatusChip';
import IncidentDataChangeDialog from '@components/Incidents/IncidentDataChangeDialog';
import s from './CustomerDetailPanel.module.css';

type CustomerIncidentsTabProps = {
  incidents: CustomerIncident[];
};

function formatAttachedAt(value: string): string {
  const fd = formatPaymentDate(new Date(value));
  return `${fd.date} · ${fd.time}`;
}

// The rows come embedded in the dashboard-customers aggregate (like debt/disputes), so this tab has
// no fetch of its own; every incident links out to its journal page for the full customer list.
function CustomerIncidentsTab({ incidents }: CustomerIncidentsTabProps) {
  return (
    <div className={s.tableCard}>
      {incidents.length === 0 ? (
        <div className={s.tabEmpty}>This customer is not attached to any incident.</div>
      ) : (
        <table className={s.miniTable}>
          <thead>
            <tr>
              <th>Incident</th>
              <th>Status</th>
              <th>Note</th>
              <th>Source</th>
              <th>Data</th>
              <th>Attached</th>
            </tr>
          </thead>
          <tbody>
            {incidents.map((incident) => (
              <tr key={incident.id}>
                <td>
                  <Link to={getIncidentUrl(incident.id)} className={s.incidentLink}>
                    <span>{incident.name}</span>
                    <OpenInNewIcon sx={{ fontSize: 13 }} aria-hidden />
                  </Link>
                </td>
                <td>
                  <StatusChip
                    label={INCIDENT_STATUS_LABELS[incident.status] ?? incident.status}
                    tone={getIncidentStatusTone(incident.status)}
                  />
                </td>
                <td className={s.incidentNote} title={incident.note ?? undefined}>
                  {incident.note || '—'}
                </td>
                <td className={s.mono}>{incident.source || '—'}</td>
                <td>
                  <IncidentDataChangeDialog
                    subject={incident.name}
                    previousData={incident.previousData}
                    newData={incident.newData}
                  />
                </td>
                <td>{formatAttachedAt(incident.attachedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default CustomerIncidentsTab;
